import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ExternalLink, Code, Eye } from "lucide-react";

// Demo popup for premium features
function DemoPopup({ open, onClose }) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <span>✨</span>
            Premium Feature
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            This feature is only available on the paid platform. Contact us to get started!
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center pt-4">
          <Button onClick={onClose} className="px-8">Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Embed Code Modal for a custom widget
function EmbedCodeModal({ open, widget, onClose, onCopyAttempt }){
  if(!open || !widget) return null;
  const [bg,setBg]=useState('#ffffff');
  const [width,setWidth]=useState('100%');
  const [maxWidth,setMaxWidth]=useState('800px');
  const [height,setHeight]=useState('900px');
  const [padding,setPadding]=useState('12px');
  const [hAlign,setHAlign]=useState('center');
  const [vAlign,setVAlign]=useState('top');
  
  const base = typeof window!=='undefined'? window.location.origin: '';
  const src = `${base}${widget.path}`;
  
  const getContainerStyle = () => {
    const styles = [`background:${bg}`];
    styles.push('display:flex');
    if (hAlign === 'left') styles.push('justify-content:flex-start');
    else if (hAlign === 'right') styles.push('justify-content:flex-end');
    else styles.push('justify-content:center');
    if (vAlign === 'top') styles.push('align-items:flex-start');
    else if (vAlign === 'bottom') styles.push('align-items:flex-end');
    else styles.push('align-items:center');
    styles.push(`padding:${padding}`);
    return styles.join(';');
  };
  
  const containerStyle = getContainerStyle();
  const code = `<div style="${containerStyle}">
  <iframe src="${src}" style="width:${width};${maxWidth !== 'none' ? `max-width:${maxWidth};` : ''}height:${height};border:0;border-radius:8px;box-shadow:0 4px 6px -1px rgba(0,0,0,0.1);" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
</div>`;

  const previewHClass = hAlign === 'left' ? 'justify-start' : hAlign === 'right' ? 'justify-end' : 'justify-center';
  const previewVClass = vAlign === 'top' ? 'items-start' : vAlign === 'bottom' ? 'items-end' : 'items-center';
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="embed-modal" onClick={(e)=>e.target===e.currentTarget && onClose()}>
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between sticky top-0">
          <div>
            <h2 className="text-white font-semibold text-lg">Embed: {widget.title}</h2>
            <p className="text-blue-100 text-sm">Add this widget to your website</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors p-1" type="button">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">iframe Size</h3>
              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs text-gray-500 mb-1 block">Width</span>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={width} onChange={(e)=>setWidth(e.target.value)} placeholder="100%" />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500 mb-1 block">Max Width</span>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={maxWidth} onChange={(e)=>setMaxWidth(e.target.value)} placeholder="800px" />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500 mb-1 block">Height</span>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={height} onChange={(e)=>setHeight(e.target.value)} placeholder="900px" />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Background</h3>
                <div className="flex items-center gap-2">
                  <input type="color" value={bg} onChange={(e)=>setBg(e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border-2 border-gray-200" />
                  <input className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono bg-gray-50" value={bg} onChange={(e)=>setBg(e.target.value)} placeholder="#ffffff" />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Padding</h3>
                <input className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50" value={padding} onChange={(e)=>setPadding(e.target.value)} placeholder="12px" />
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Position</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-gray-500 mb-2 block">Horizontal</span>
                  <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                    {[['left','Left'],['center','Center'],['right','Right']].map(([val,label])=>(
                      <button type="button" key={val} onClick={()=>setHAlign(val)} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${hAlign===val?'bg-white shadow text-blue-600':'text-gray-600 hover:text-gray-900'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-500 mb-2 block">Vertical</span>
                  <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                    {[['top','Top'],['middle','Middle'],['bottom','Bottom']].map(([val,label])=>(
                      <button type="button" key={val} onClick={()=>setVAlign(val)} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${vAlign===val?'bg-white shadow text-blue-600':'text-gray-600 hover:text-gray-900'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Embed Code</h3>
              <textarea readOnly value={code} className="w-full h-28 border rounded-lg p-3 font-mono text-xs bg-gray-900 text-green-400 resize-none" />
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Preview</h3>
            <div className="border-2 border-dashed border-gray-200 rounded-xl overflow-hidden bg-gray-50 p-2">
              <div className="text-xs text-gray-400 mb-1 text-center">Your webpage</div>
              <div className={`flex ${previewHClass} ${previewVClass} rounded-lg border shadow-inner overflow-hidden transition-all duration-300`} style={{ height: '320px', backgroundColor: bg, padding: padding }}>
                <div className="bg-white rounded-lg shadow-lg flex flex-col overflow-hidden" style={{ width: '200px', minHeight: '280px' }}>
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-2 flex-shrink-0 rounded-t-lg">
                    <div className="w-20 h-2 bg-white/40 rounded mb-1"></div>
                    <div className="w-12 h-1.5 bg-white/25 rounded"></div>
                  </div>
                  <div className="p-2 space-y-1.5 bg-white flex-1 rounded-b-lg">
                    <div className="w-full h-4 bg-gray-100 rounded"></div>
                    <div className="w-full h-4 bg-gray-100 rounded"></div>
                    <div className="w-3/4 h-4 bg-gray-100 rounded"></div>
                    <div className="w-full h-6 bg-gray-100 rounded mt-2"></div>
                    <div className="w-full h-4 bg-blue-500 rounded mt-2"></div>
                  </div>
                </div>
              </div>
              <div className="text-center mt-2">
                <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-white px-2 py-1 rounded-full border">
                  Position: {hAlign} / {vAlign}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t sticky bottom-0">
          <div className="text-xs text-gray-500">
            Paste this code where you want the widget to appear.
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition">Close</button>
            <button type="button" onClick={onCopyAttempt} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition shadow-sm">
              Copy Embed Code
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CustomWidgets() {
  const [showFeaturePopup, setShowFeaturePopup] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);
  
  // List of custom widgets
  const customWidgets = [
    {
      id: "functional-demo",
      title: "Functional Demo",
      description: "A fully interactive demo of the campaign creation interface. Shows the complete Create Campaign form with all options, theme customization, live widget preview, and Active Campaigns table.",
      path: "/functional-demo",
      stats: { views: 150, sent: 88, leads: 76 }
    },
    {
      id: "election-simulator",
      title: "Election Simulator",
      description: "Australian Federal Election simulator with primary vote sliders, House of Reps & Senate projections, preference flow customization, and shareable results. Full STV calculation model.",
      path: "/election-simulator",
      stats: { views: 0, sent: 0, leads: 0 }
    },
    {
      id: "gis-electorate-lookup",
      title: "Electorate Lookup",
      description: "Enter any Australian address and instantly reveal the federal House of Reps and state electorate it sits inside. Powered by ABS polygon GIS data + free OpenStreetMap geocoding — no API keys, no per-lookup fees.",
      path: "/gis-test",
      stats: { views: 0, sent: 0, leads: 0 }
    }
  ];
  
  const openEmbedModal = (widget) => {
    setSelectedWidget(widget);
    setEmbedOpen(true);
  };
  
  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="custom-widgets-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#171717]" style={{ fontFamily: "'Playfair Display', serif" }}>Custom Widgets</h1>
        <p className="text-sm text-gray-500">Your embeddable custom widgets for showcasing the platform.</p>
      </div>
      
      {/* Widgets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {customWidgets.map((widget) => (
          <Card key={widget.id} className="hover:shadow-lg transition-shadow" data-testid={`widget-card-${widget.id}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-xl">{widget.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">{widget.description}</p>
              
              <div className="flex gap-4 text-xs text-gray-500 pt-2 border-t">
                <span title="Sent">📧 {widget.stats.sent} sent</span>
                <span title="Leads">👤 {widget.stats.leads} leads</span>
              </div>
              
              <div className="flex flex-col gap-2 pt-2">
                <a 
                  href={widget.path} 
                  target="_blank" 
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white h-10 px-4 transition-colors"
                  data-testid={`go-to-demo-${widget.id}`}
                >
                  <Eye className="w-4 h-4" />
                  Go to the Demo
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
                <Button 
                  variant="outline" 
                  className="w-full gap-2"
                  onClick={() => openEmbedModal(widget)}
                  data-testid={`embed-code-${widget.id}`}
                >
                  <Code className="w-4 h-4" />
                  Copy Embed Code
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <DemoPopup open={showFeaturePopup} onClose={() => setShowFeaturePopup(false)} />
      
      <EmbedCodeModal 
        open={embedOpen} 
        widget={selectedWidget}
        onClose={() => setEmbedOpen(false)}
        onCopyAttempt={() => {
          setEmbedOpen(false);
          setShowFeaturePopup(true);
        }}
      />
    </div>
  );
}
